import TabButtons, { TTabButtonOption } from '@/components/atoms/TabButtons';
import AppPageTemplate from '@/components/templates/AppPageTemplate';
import LoadingPage from '@/components/templates/LoadingPage';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { MEMBER_PROFILE_SECTIONS } from '@/constants/stringVars';
import { useMemberCareMemberProfile, useMemberCareMemberTimeline } from '@/hooks/member-care/memberCare';
import MemberCareMemberWellnessTimeline from '@/modules/memberCare/MemberCareMemberWellnessTimeline';
import MemberInfoHeader from '@/modules/memberCare/memberId/MemberInfoHeader';
import MemberTab from '@/modules/memberCare/memberId/tabs/Member';
import NotesTab from '@/modules/memberCare/memberId/tabs/Notes';
import ScreenersTab from '@/modules/memberCare/memberId/tabs/Screeners';
import { TMemberProfileSection } from '@/types/MemberCare.types';
import { VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

const MemberProfileView = () => {
  const { memberCareMemberId } = Route.useParams();

  const [selectedProfileSection, setSelectedProfileSection] = useState<TMemberProfileSection>('member');

  const { data: member, isLoading } = useMemberCareMemberProfile(memberCareMemberId);

  const {
    data: memberTimelineItems,
    isLoading: isLoadingTimeline,
    refetch: refetchTimelineItems,
  } = useMemberCareMemberTimeline(memberCareMemberId);

  const memberProfileSectionTabs: TTabButtonOption[] = MEMBER_PROFILE_SECTIONS.map((section) => ({
    labelDisplay: section.display,
    labelValue: section.value,
    onClick: () => setSelectedProfileSection(section.value),
  }));

  if (isLoading || isLoadingTimeline || !member || !memberTimelineItems) return <LoadingPage />;

  return (
    <AppPageTemplate>
      <VStack width={MAX_CONTAINER_WIDTH} paddingBottom={'100px'}>
        <VStack width={'100%'} marginBottom={'50px'}>
          <MemberInfoHeader member={member} />
          <VStack marginBottom={'52px'}>
            <TabButtons options={memberProfileSectionTabs} selectedOptionValue={selectedProfileSection} />
          </VStack>

          {selectedProfileSection === 'member' && <MemberTab member={member} />}
          {selectedProfileSection === 'notes' && (
            <NotesTab
              memberId={memberCareMemberId}
              member={member}
              onObservationsUpdate={refetchTimelineItems}
              onNotesUpdate={refetchTimelineItems}
            />
          )}
          {selectedProfileSection === 'screeners' && <ScreenersTab member={member} memberId={memberCareMemberId} />}
          <VStack marginTop={'120px'}>
            <MemberCareMemberWellnessTimeline firstName={member.firstName} memberTimelineItems={memberTimelineItems} />
          </VStack>
        </VStack>
      </VStack>
    </AppPageTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_member-care/member-care/members/$memberCareMemberId')({
  component: MemberProfileView,
});
